import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { ArrowLeft, Table2, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dqp_token')}` } });

function StatBar({ value, max, color = '#5b63ea' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full bg-[#f0f1f5] rounded-full h-2">
      <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

export default function SchemaProfiler() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/datasources/${id}/profile`, authH());
      setData(d);
      if (d.schema?.tables?.length > 0) setSelectedTable(d.schema.tables[0].name);
    } catch {}
  }, [id]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  if (!data) return <DashboardLayout><div className="p-8 text-[#6b7280]">Loading profile...</div></DashboardLayout>;

  const tables = data.schema?.tables || [];
  const profiles = data.profiles || {};
  const currentTable = tables.find(t => t.name === selectedTable);
  const currentProfile = profiles[selectedTable] || {};

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/app/data-sources">
            <Button variant="outline" size="sm" className="border-[#e6e8f2] text-[#6b7280]"><ArrowLeft size={14} className="mr-1" /> Back</Button>
          </Link>
          <div>
            <h1 data-testid="profiler-title" className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>Schema Profiler</h1>
            <p className="text-[#6b7280] text-sm">Column-level data profiling and statistics</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Table List */}
          <div className="space-y-1">
            {tables.map(t => (
              <button
                key={t.name}
                data-testid={`profile-table-${t.name}`}
                onClick={() => setSelectedTable(t.name)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left text-sm transition-all ${
                  selectedTable === t.name
                    ? 'bg-[#5b63ea]/10 text-[#5b63ea] font-medium'
                    : 'text-[#6b7280] hover:bg-[#f5f6fb]'
                }`}
              >
                <Table2 size={14} />
                <span>{t.name}</span>
                <span className="ml-auto text-xs opacity-60">{t.row_count}</span>
              </button>
            ))}
          </div>

          {/* Profile Detail */}
          <div className="lg:col-span-3">
            {currentTable ? (
              <div className="space-y-4">
                {/* Table Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Rows', value: currentTable.row_count?.toLocaleString() },
                    { label: 'Columns', value: currentTable.columns?.length },
                    { label: 'Relationships', value: currentTable.columns?.filter(c => c.is_fk).length },
                    { label: 'Nullable', value: currentTable.columns?.filter(c => c.nullable).length },
                  ].map((s, i) => (
                    <div key={i} className="bg-white rounded-xl border border-[#e6e8f2] p-4">
                      <p className="text-xs text-[#9ca3af]">{s.label}</p>
                      <p className="text-xl font-bold text-[#1e1e2e] mt-1" style={{ fontFamily: 'Outfit' }}>{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Column Profiles */}
                <div className="bg-white rounded-xl border border-[#e6e8f2] overflow-hidden">
                  <div className="px-5 py-4 border-b border-[#e6e8f2]">
                    <h2 className="font-semibold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>Column Profiles — {selectedTable}</h2>
                  </div>
                  <div className="scrollbar-light overflow-x-auto">
                    <table data-testid="profile-table" className="w-full">
                      <thead>
                        <tr className="bg-[#f5f6fb] border-b border-[#e6e8f2]">
                          <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Column</th>
                          <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Type</th>
                          <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Distinct</th>
                          <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Null %</th>
                          <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Min / Max</th>
                          <th className="text-left px-5 py-2.5 text-[10px] uppercase tracking-wider text-[#9ca3af] font-semibold">Top Values</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTable.columns?.map(col => {
                          const prof = currentProfile[col.name];
                          return (
                            <tr key={col.name} className="border-b border-[#f0f1f5] hover:bg-[#f5f6fb]/50">
                              <td className="px-5 py-3">
                                <span className="text-sm font-mono font-medium text-[#1e1e2e]">{col.name}</span>
                                {col.is_pk && <span className="ml-1.5 text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded">PK</span>}
                                {col.is_fk && <span className="ml-1.5 text-[9px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded">FK</span>}
                              </td>
                              <td className="px-5 py-3 text-xs font-mono text-[#9ca3af]">{col.type}</td>
                              <td className="px-5 py-3 text-sm text-[#1e1e2e]">{prof?.distinct_count ?? '-'}</td>
                              <td className="px-5 py-3">
                                {prof ? (
                                  <div className="flex items-center gap-2 min-w-[100px]">
                                    <StatBar value={prof.null_pct} max={100} color={prof.null_pct > 20 ? '#f59e0b' : '#10b981'} />
                                    <span className="text-xs text-[#6b7280] w-10 text-right">{prof.null_pct}%</span>
                                  </div>
                                ) : '-'}
                              </td>
                              <td className="px-5 py-3 text-xs text-[#6b7280] font-mono">
                                {prof?.min !== undefined ? `${prof.min} / ${prof.max}` : '-'}
                              </td>
                              <td className="px-5 py-3">
                                {prof?.top_values?.slice(0, 3).map((v, i) => (
                                  <span key={i} className="inline-block bg-[#f5f6fb] text-[#6b7280] text-[10px] px-1.5 py-0.5 rounded mr-1 mb-0.5 font-mono">
                                    {String(v.value).substring(0, 20)}{String(v.value).length > 20 ? '...' : ''} ({v.count})
                                  </span>
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-[#e6e8f2] p-12 text-center">
                <BarChart3 size={48} className="mx-auto text-[#d4d8e6] mb-3" />
                <p className="text-[#6b7280]">Select a table to view its profile</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
